import { useEffect, useState } from "react";
import { Loader2, Plus, Pencil, Trash2, Check, X, Globe, AlertCircle } from "lucide-react";
import api, { formatDetail } from "@/lib/api";

/**
 * Partner-level master list of "websites" (branches / properties).
 *
 * A website is a tag — it lets the partner group their successful
 * payments by the downstream property the customer was paying for
 * (e.g. cricket-bet1.com vs football-spot.com vs casino-royale.com).
 *
 * The same UPI / bank pool serves all websites; this is purely a
 * tagging surface. Customers self-select at checkout (per-link allowlist
 * + per-link default supported in the Edit Payment Link modal).
 *
 * CRUD: name-only entries. Soft-deleted from links' allowlists on
 * deletion so the customer never sees a stale entry.
 */
export default function PartnerWebsites() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [newName, setNewName] = useState("");
    const [busy, setBusy] = useState(false);
    const [editId, setEditId] = useState(null);
    const [editName, setEditName] = useState("");

    const load = async () => {
        try {
            const { data } = await api.get("/partner/websites");
            setItems(data.items || []);
            setError(null);
        } catch (e) {
            setError(formatDetail(e));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const addWebsite = async (e) => {
        e?.preventDefault();
        const name = newName.trim();
        if (!name || busy) return;
        setBusy(true);
        try {
            await api.post("/partner/websites", { name });
            setNewName("");
            await load();
        } catch (e) {
            setError(formatDetail(e));
        } finally { setBusy(false); }
    };

    const saveEdit = async (websiteId) => {
        const name = editName.trim();
        if (!name) { setEditId(null); return; }
        setBusy(true);
        try {
            await api.patch(`/partner/websites/${websiteId}`, { name });
            setEditId(null);
            setEditName("");
            await load();
        } catch (e) {
            setError(formatDetail(e));
        } finally { setBusy(false); }
    };

    const toggleStatus = async (w) => {
        setBusy(true);
        try {
            await api.patch(`/partner/websites/${w.website_id}`, {
                status: w.status === "active" ? "disabled" : "active",
            });
            await load();
        } catch (e) {
            setError(formatDetail(e));
        } finally { setBusy(false); }
    };

    const remove = async (w) => {
        // Confirmation only on hard-delete — toggling is the soft path.
        if (!window.confirm(
            `Delete website "${w.name}"? Historical transactions tagged with this website will keep their name snapshot, but it will be removed from all payment-link allowlists.`,
        )) return;
        setBusy(true);
        try {
            await api.delete(`/partner/websites/${w.website_id}`);
            await load();
        } catch (e) {
            setError(formatDetail(e));
        } finally { setBusy(false); }
    };

    return (
        <div className="space-y-5 max-w-3xl">
            <div className="flex items-center gap-2.5">
                <Globe className="w-5 h-5 text-slate-700" />
                <div>
                    <h1 className="text-xl font-bold text-slate-900">Websites</h1>
                    <p className="text-xs text-slate-500">Branches / properties shown at checkout so customers self-select which site they&apos;re paying for. Per-link allowlist + default in the Payment Link settings.</p>
                </div>
            </div>

            {/* Add row */}
            <form onSubmit={addWebsite} className="flex gap-2 items-stretch">
                <input
                    data-testid="website-new-name"
                    type="text"
                    placeholder="e.g. Cricket Bet 1"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    maxLength={80}
                    className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                />
                <button
                    data-testid="website-add-btn"
                    type="submit"
                    disabled={busy || !newName.trim()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-1.5"
                >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                    Add website
                </button>
            </form>

            {error && (
                <div className="flex items-start gap-2 p-3 bg-rose-50 border border-rose-200 rounded-lg text-xs text-rose-700">
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div>{error}</div>
                </div>
            )}

            {loading ? (
                <div className="text-center py-8 text-slate-400 text-sm"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading…</div>
            ) : items.length === 0 ? (
                <div data-testid="websites-empty" className="text-center py-12 border-2 border-dashed border-slate-200 rounded-lg">
                    <Globe className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                    <div className="text-sm font-medium text-slate-600">No websites yet</div>
                    <div className="text-xs text-slate-400 mt-0.5">Add your first website above to enable checkout-time selection.</div>
                </div>
            ) : (
                <div data-testid="websites-list" className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                            <tr>
                                <th className="text-left px-4 py-2.5 font-semibold">Name</th>
                                <th className="text-left px-4 py-2.5 font-semibold w-24">Status</th>
                                <th className="text-right px-4 py-2.5 font-semibold w-44">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((w) => (
                                <tr key={w.website_id} data-testid={`website-row-${w.website_id}`}
                                    className="border-t border-slate-100 hover:bg-slate-50/40">
                                    <td className="px-4 py-2.5">
                                        {editId === w.website_id ? (
                                            <input
                                                data-testid={`website-edit-input-${w.website_id}`}
                                                type="text"
                                                autoFocus
                                                value={editName}
                                                onChange={(e) => setEditName(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") saveEdit(w.website_id);
                                                    if (e.key === "Escape") setEditId(null);
                                                }}
                                                className="w-full px-2 py-1 border border-blue-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                                            />
                                        ) : (
                                            <span className="font-medium text-slate-800">{w.name}</span>
                                        )}
                                        <div className="text-[10px] text-slate-400 font-mono mt-0.5">{w.website_id}</div>
                                    </td>
                                    <td className="px-4 py-2.5">
                                        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-semibold ${w.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>
                                            {w.status}
                                        </span>
                                    </td>
                                    <td className="px-4 py-2.5 text-right">
                                        {editId === w.website_id ? (
                                            <div className="inline-flex items-center gap-1">
                                                <button data-testid={`website-edit-save-${w.website_id}`} onClick={() => saveEdit(w.website_id)} className="p-1.5 hover:bg-emerald-100 text-emerald-700 rounded" title="Save">
                                                    <Check className="w-4 h-4" />
                                                </button>
                                                <button data-testid={`website-edit-cancel-${w.website_id}`} onClick={() => setEditId(null)} className="p-1.5 hover:bg-slate-200 text-slate-600 rounded" title="Cancel">
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="inline-flex items-center gap-1">
                                                <button data-testid={`website-edit-${w.website_id}`} onClick={() => { setEditId(w.website_id); setEditName(w.name); }} className="p-1.5 hover:bg-slate-100 text-slate-600 rounded" title="Rename">
                                                    <Pencil className="w-3.5 h-3.5" />
                                                </button>
                                                <button data-testid={`website-toggle-${w.website_id}`} onClick={() => toggleStatus(w)} disabled={busy} className="px-2 py-1 text-[11px] border border-slate-200 hover:bg-slate-50 text-slate-600 rounded">
                                                    {w.status === "active" ? "Disable" : "Enable"}
                                                </button>
                                                <button data-testid={`website-delete-${w.website_id}`} onClick={() => remove(w)} disabled={busy} className="p-1.5 hover:bg-rose-100 text-rose-600 rounded" title="Delete">
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
